import {
  loadCommitFileRows,
  loadCommitFileContents,
  loadCommitFileRow,
} from "./commitFileRows";
import { copyCommitFileReferences, materializeCommitStep } from "./stepTrees";
import type { Transaction } from "@rocicorp/zero";
import { Cron } from "croner";
import { apiFragmentPath, validateApiFragment } from "./apiFragment";
import { lockCommitFacets } from "./commitFacetLock";
import { refreshConnectorUsages } from "./refreshConnectorUsages";
import { userFacingError } from "./authorization";
import { getWorkflowIdForCommit, resolveSlug } from "./connectorSlug";
import { dropConnectorUsage, usageRef } from "./connectorUsages";
import { zql } from "./generatedDrizzleSchema";
import { type InsertRow, insertRows, serverTable } from "./insertRows";
import { isValidGitPath } from "./gitPath";
import { assertConnectorPersonalUse } from "./personalUseGuard";
import { assertSupplierAvailable } from "./rbac/crossResourceAvailability";
import { workflowReadmeDescription } from "./readmeDescription";
import { isReservedRoute } from "./reservedRoutes";
import {
  composeStepConfig,
  defaultStepConfig,
  isStepTestPath,
  parseStepConfig,
  type StepConfig,
  stepDirFromPath,
  tryParseStepConfig,
} from "./stepConfig";
import { stepContentHashFromFiles } from "./stepTests/freshness";
import { stripNullBytes } from "./stripNullBytes";
import { hasConflictMarkers } from "./threeWayMerge";
import {
  extractStepDependencies,
  isDependencySourceFile,
} from "./workflowDependencies";

type Ctx = { tx: Transaction; commitId: string; tenantId?: string };
type WriteCtx = Ctx & { tenantId: string };
type StepFile = { path: string; content: string };

export const MAX_WORKFLOW_FILES = 1000;

export async function hashBlob(raw: string) {
  const content = stripNullBytes(raw);
  const buf = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(content)
  );
  const hash = [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
  return { hash, content };
}

// Returns "" when the step has no files — won’t match any execution row.
export async function getStepContentHash(
  tx: Transaction,
  commitId: string,
  stepDir: string
): Promise<string> {
  const files = await loadCommitFileRows(tx, commitId);
  return stepContentHashFromFiles(files, stepDir);
}

async function upsertBlob(tx: Transaction, content: string, tenantId: string) {
  const { hash, content: clean } = await hashBlob(content);
  const [existing] = await tx.run(
    zql.blobs.where("hash", hash).where("tenantId", tenantId)
  );
  if (!existing)
    await tx.mutate.blobs.upsert({ hash, content: clean, tenantId });
  return hash;
}

// Git can’t store a path as both a file and a folder, so one collision breaks
// Git sync for the whole space.
async function assertNoFileFolderCollision(c: Ctx, path: string) {
  const segments = path.split("/");
  const parents = segments
    .slice(1)
    .map((_, i) => segments.slice(0, i + 1).join("/"));
  const clashes =
    c.tx.location === "server"
      ? await c.tx.dbTransaction.query(
          "SELECT path FROM resolved_commit_files WHERE commit_id = $1 AND (path = ANY($2::text[]) OR starts_with(path, $3)) LIMIT 1",
          [c.commitId, parents, `${path}/`]
        )
      : (await loadCommitFileRows(c.tx, c.commitId)).filter(
          (file) =>
            file.path.startsWith(`${path}/`) || parents.includes(file.path)
        );
  const [clash] = clashes as Iterable<{ path: string }>;
  if (!clash) return;
  throw userFacingError(
    parents.includes(clash.path)
      ? `Can’t write “${path}”: “${clash.path}” is a file, so it can’t also be a folder.`
      : `Can’t write “${path}”: it’s already a folder containing “${clash.path}”.`
  );
}

export async function writeCommitFile(
  c: WriteCtx,
  path: string,
  content: string
) {
  const { tx, commitId, tenantId } = c;
  if (!isValidGitPath(path)) {
    throw userFacingError(`Invalid workflow file path: ${path}`);
  }
  await lockCommitFacets(tx, commitId);
  await assertNoFileFolderCollision(c, path);
  const blobHash = await upsertBlob(tx, content, tenantId);
  const existing = await loadCommitFileRow(tx, commitId, path);
  if (existing?.blobHash === blobHash) return;
  await materializeCommitStep(tx, commitId, path);
  await tx.mutate.commitFiles.upsert({ commitId, path, blobHash, tenantId });
  if (path === "README.md")
    await tx.mutate.workflowCommits.update({
      id: commitId,
      readmeDescription: workflowReadmeDescription(stripNullBytes(content)),
    });
}

// A later file wins on a repeated path.
export async function deriveCommitFileRows(
  files: StepFile[],
  commitId: string,
  tenantId: string
) {
  const blobs = new Map<string, { hash: string; content: string }>();
  const commitFiles = new Map<string, { path: string; blobHash: string }>();
  for (const file of files) {
    const { hash, content } = await hashBlob(file.content);
    blobs.set(hash, { hash, content });
    commitFiles.set(file.path, { path: file.path, blobHash: hash });
  }
  return {
    blobs: [...blobs.values()].map((b) => ({ ...b, tenantId })),
    commitFiles: [...commitFiles.values()].map((f) => ({
      ...f,
      commitId,
      tenantId,
    })),
  };
}

export async function listCommitFilePaths(c: Ctx): Promise<string[]> {
  const files = await loadCommitFileRows(c.tx, c.commitId);
  return files.map((f) => `/${f.path}`).sort();
}

// Ordered by the database collation, which can differ from
// listCommitFilePaths' JS sort for mixed-case paths.
export async function listCommitFilePathPage(
  c: Ctx,
  page: { limit?: number; after?: string }
): Promise<string[]> {
  if (c.tx.location === "server") {
    const rows = await c.tx.dbTransaction.query(
      "SELECT path FROM resolved_commit_files WHERE commit_id = $1 AND ($2::text IS NULL OR path > $2) ORDER BY path LIMIT $3",
      [c.commitId, page.after?.slice(1) ?? null, page.limit ?? null]
    );
    return Array.from(rows, (row) => `/${row.path}`);
  }
  const files = await loadCommitFileRows(c.tx, c.commitId);
  return files
    .map((file) => `/${file.path}`)
    .sort()
    .filter((path) => !page.after || path > page.after)
    .slice(0, page.limit);
}

export async function readCommitFileContent(c: Ctx, path: string) {
  const { tx, commitId } = c;
  const file = await loadCommitFileRow(tx, commitId, path);
  if (!file) return null;
  const [blob] = await tx.run(
    zql.blobs.where("hash", file.blobHash).where("tenantId", file.tenantId)
  );
  return blob?.content ?? null;
}

export async function findConflictedFiles(c: Ctx): Promise<string[]> {
  const { tx, commitId } = c;
  const files = await loadCommitFileContents(tx, commitId);
  return files
    .filter((f) => f.blob && hasConflictMarkers(f.blob.content))
    .map((f) => f.path)
    .sort();
}

export async function copyAllCommitFiles(c: WriteCtx, toCommitId: string) {
  await copyCommitFileReferences(c, toCommitId);
  return syncAllFacets({ ...c, commitId: toCommitId });
}

export async function deleteStepDir(c: Ctx, stepDir: string) {
  const { tx, commitId } = c;
  await lockCommitFacets(tx, commitId);
  await tx.mutate.commitSteps.delete({ commitId, stepDir });
  const allFiles = await loadCommitFileRows(tx, commitId);
  for (const f of allFiles) {
    if (stepDirFromPath(f.path) === stepDir)
      await tx.mutate.commitFiles.delete({ commitId, path: f.path });
  }
  await clearStepFacets(c, stepDir);
}

export async function readStepConfig(
  c: Ctx,
  stepDir: string
): Promise<StepConfig> {
  const raw = await readCommitFileContent(c, `${stepDir}/config.toml`);
  return raw ? parseStepConfig(raw) : defaultStepConfig();
}

export async function updateStepConfig(
  c: WriteCtx,
  stepDir: string,
  updates: Partial<StepConfig>
) {
  const config = await readStepConfig(c, stepDir);
  await writeCommitFile(
    c,
    `${stepDir}/config.toml`,
    composeStepConfig({ ...config, ...updates })
  );
}

export async function listStepDirs(c: Ctx): Promise<string[]> {
  const files = await loadCommitFileRows(c.tx, c.commitId);
  const dirs = new Set<string>();
  for (const f of files) {
    const dir = stepDirFromPath(f.path);
    if (dir) dirs.add(dir);
  }
  return [...dirs];
}

// Every facet of a commit is derived from its files, so a whole-commit sync
// replaces the lot.
const FACET_TABLES = [
  "commitRoutes",
  "commitSchedules",
  "commitEmailAddresses",
  "commitLinks",
  "commitDependencies",
] as const;

type FacetRows = {
  [T in (typeof FACET_TABLES)[number]]: Array<InsertRow<T>>;
} & { connectedAppUsages: Array<InsertRow<"connectedAppUsages">> };

/** Fan-out of a commit's derived facts, for publish telemetry. */
export type FacetCounts = {
  files: number;
  stepDirs: number;
  facetRows: number;
};

function emptyFacetRows(): FacetRows {
  return {
    commitRoutes: [],
    commitSchedules: [],
    commitEmailAddresses: [],
    commitLinks: [],
    commitDependencies: [],
    connectedAppUsages: [],
  };
}

async function writeFacetRows(c: WriteCtx, rows: FacetRows) {
  for (const [table, tableRows] of Object.entries(rows))
    await insertRows(c.tx, table as keyof FacetRows, tableRows as never);
}

async function syncCommitConnectorUsages(c: WriteCtx) {
  const { tx, commitId, tenantId } = c;
  const stepUsages = await tx.run(
    zql.connectedAppUsages.where("type", "commit_step").where("refId", commitId)
  );
  const connectorIds = new Set(stepUsages.map((row) => row.connectedAppId));
  const existing = await tx.run(
    zql.commitConnectorUsages.where("commitId", commitId)
  );

  for (const row of existing) {
    if (!connectorIds.delete(row.connectorId))
      await tx.mutate.commitConnectorUsages.delete({
        commitId,
        connectorId: row.connectorId,
      });
  }
  await insertRows(
    tx,
    "commitConnectorUsages",
    [...connectorIds].map((connectorId) => ({
      commitId,
      connectorId,
      tenantId,
    }))
  );
}

async function clearAllFacets(c: Ctx) {
  const { tx, commitId } = c;
  await lockCommitFacets(tx, commitId);
  if (tx.location === "server") {
    for (const table of FACET_TABLES)
      await tx.dbTransaction.query(
        `DELETE FROM ${serverTable(table)} WHERE commit_id = $1`,
        [commitId]
      );
    await tx.dbTransaction.query(
      `DELETE FROM ${serverTable("connectedAppUsages")} WHERE type = 'commit_step' AND ref_id = $1`,
      [commitId]
    );
    return;
  }
  for (const dir of await listStepDirs(c)) await clearStepFacets(c, dir);
}

async function clearStepFacets(c: Ctx, stepDir: string) {
  const { tx, commitId } = c;
  await lockCommitFacets(tx, commitId);
  for (const r of await tx.run(
    zql.commitRoutes.where("commitId", commitId).where("stepDir", stepDir)
  ))
    await tx.mutate.commitRoutes.delete({ commitId, route: r.route });
  for (const d of await tx.run(
    zql.commitDependencies.where("commitId", commitId).where("stepDir", stepDir)
  ))
    await tx.mutate.commitDependencies.delete({
      commitId,
      stepDir,
      source: d.source,
      name: d.name,
    });
  for (const l of await tx.run(
    zql.commitLinks.where("commitId", commitId).where("fromStep", stepDir)
  ))
    await tx.mutate.commitLinks.delete({
      commitId,
      fromStep: l.fromStep,
      toStep: l.toStep,
    });
  const stepRef = usageRef.commitStep(commitId, stepDir);
  for (const ca of await tx.run(
    zql.connectedAppUsages
      .where("type", stepRef.type)
      .where("refId", stepRef.refId)
      .where("subKey", stepRef.subKey)
  ))
    await dropConnectorUsage(tx, ca.connectedAppId, stepRef);
  const [schedule] = await tx.run(
    zql.commitSchedules.where("commitId", commitId).where("stepDir", stepDir)
  );
  if (schedule) await tx.mutate.commitSchedules.delete({ commitId, stepDir });
  for (const e of await tx.run(
    zql.commitEmailAddresses
      .where("commitId", commitId)
      .where("stepDir", stepDir)
  ))
    await tx.mutate.commitEmailAddresses.delete({
      commitId,
      localPart: e.localPart,
    });
}

/** `space` only when each space has its own subdomain; `tenant` otherwise. */
export type RouteScope = "space" | "tenant";

/**
 * Call from write sites only (never copy paths — the source was already
 * validated when written). `commitId` identifies the workflow to exclude.
 */
export async function assertRoutesAvailable(
  tx: Transaction,
  commitId: string,
  incomingRoutes: Iterable<string>,
  scope: RouteScope
) {
  const incoming = new Set(incomingRoutes);
  if (incoming.size === 0) return;
  const [self] = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("branch", (b) => b.related("workflow"))
  );
  const w = self?.branch?.workflow;
  if (!w) return;
  const base = zql.workflowBranches
    .where("name", "main")
    .where("workflowId", "!=", w.id)
    .where("tenantId", w.tenantId)
    .related("headCommit", (c) => c.related("routes"));
  const scoped = base.whereExists("workflow", (x) => {
    const live = x.where("deletedAt", "IS", null);
    return scope === "space" ? live.where("spaceId", w.spaceId) : live;
  });
  const mains = await tx.run(scoped);
  for (const mb of mains) {
    const collision = mb.headCommit?.routes.find((r) => incoming.has(r.route));
    if (collision)
      throw userFacingError(
        `Route "${collision.route}" is already used by another live workflow.`
      );
  }
}

export async function assertStepRoutesAvailable(
  c: Ctx,
  stepDirs: Iterable<string>,
  scope: RouteScope
) {
  const routes: string[] = [];
  for (const dir of stepDirs) {
    const config = await readStepConfig(c, dir);
    if (config.route) routes.push(config.route);
  }
  await assertRoutesAvailable(c.tx, c.commitId, routes, scope);
}

export async function assertEmailAddressesAvailable(
  tx: Transaction,
  commitId: string,
  incomingLocalParts: Iterable<string>,
  scope: RouteScope
) {
  const incoming = new Set(incomingLocalParts);
  if (incoming.size === 0) return;
  const [self] = await tx.run(
    zql.workflowCommits
      .where("id", commitId)
      .related("branch", (b) => b.related("workflow"))
  );
  const w = self?.branch?.workflow;
  if (!w) return;
  const base = zql.workflowBranches
    .where("name", "main")
    .where("workflowId", "!=", w.id)
    .where("tenantId", w.tenantId)
    .related("headCommit", (c) => c.related("emailAddresses"));
  const scoped = base.whereExists("workflow", (x) => {
    const live = x.where("deletedAt", "IS", null);
    return scope === "space" ? live.where("spaceId", w.spaceId) : live;
  });
  const mains = await tx.run(scoped);
  for (const mb of mains) {
    const collision = mb.headCommit?.emailAddresses.find((e) =>
      incoming.has(e.localPart)
    );
    if (collision) {
      const where = scope === "space" ? "in this space" : "on this deployment";
      throw userFacingError(
        `Email address "${collision.localPart}" is already used by another live workflow ${where}.`
      );
    }
  }
}

export async function assertStepEmailAddressesAvailable(
  c: Ctx,
  stepDirs: Iterable<string>,
  scope: RouteScope
) {
  const localParts: string[] = [];
  for (const dir of stepDirs) {
    const config = await readStepConfig(c, dir);
    if (config.emailAddress) localParts.push(config.emailAddress);
  }
  await assertEmailAddressesAvailable(c.tx, c.commitId, localParts, scope);
}

async function loadFacetSnapshot(c: WriteCtx) {
  const files = await loadCommitFileRows(c.tx, c.commitId);
  const hashes = [...new Set(files.map((f) => f.blobHash))];
  const blobs =
    hashes.length > 0
      ? await c.tx.run(
          zql.blobs.where("hash", "IN", hashes).where("tenantId", c.tenantId)
        )
      : [];
  const contentByHash = new Map(blobs.map((b) => [b.hash, b.content]));
  const filesByStep = new Map<string, (typeof files)[number][]>();
  const contentByPath = new Map<string, string>();
  for (const file of files) {
    const stepDir = stepDirFromPath(file.path);
    if (stepDir) {
      const stepFiles = filesByStep.get(stepDir) ?? [];
      stepFiles.push(file);
      filesByStep.set(stepDir, stepFiles);
    }
    const content = contentByHash.get(file.blobHash);
    if (content !== undefined) contentByPath.set(file.path, content);
  }
  return { filesByStep, contentByPath };
}

type FacetSnapshot = Awaited<ReturnType<typeof loadFacetSnapshot>>;

/** Which step holds each route and email address, as derivation proceeds. */
type Claims = { routes: Map<string, string>; emails: Map<string, string> };

// A single-step sync leaves the other steps' facets in place, so it has to know
// what they hold or it would miss a collision with them. A whole-commit sync
// replaces the lot and starts from nothing.
async function claimsFromDb(c: Ctx): Promise<Claims> {
  const { tx, commitId } = c;
  const routes = await tx.run(zql.commitRoutes.where("commitId", commitId));
  const emails = await tx.run(
    zql.commitEmailAddresses.where("commitId", commitId)
  );
  return {
    routes: new Map(routes.map((r) => [r.route, r.stepDir])),
    emails: new Map(emails.map((e) => [e.localPart, e.stepDir])),
  };
}

function connectorResolver(c: WriteCtx) {
  const loadWorkflow = async () => {
    const workflowId = await getWorkflowIdForCommit(c.tx, c.commitId);
    const [wf] = await c.tx.run(
      zql.workflows.where("id", workflowId).related("space")
    );
    return { workflowId, space: wf?.space };
  };
  let workflow: ReturnType<typeof loadWorkflow> | undefined;
  const resolved = new Map<string, Promise<string | undefined>>();
  const resolve = async (name: string) => {
    const { workflowId, space } = await (workflow ??= loadWorkflow());
    const connectedAppId = (await resolveSlug(c.tx, workflowId, name)) ?? name;
    const [app] = await c.tx.run(zql.connectedApps.where("id", connectedAppId));
    if (!app) return undefined;
    if (space) {
      await assertSupplierAvailable({
        tx: c.tx,
        tenantId: c.tenantId,
        ruleId: "space_uses_connected_apps",
        dependentScopeId: space.id,
        supplierScopeId: connectedAppId,
        message: `Missing connector access: ${app.name}`,
      });
      assertConnectorPersonalUse(app, { kind: "space", mode: space.mode });
    }
    return connectedAppId;
  };
  return (name: string) => {
    const pending = resolved.get(name) ?? resolve(name);
    resolved.set(name, pending);
    return pending;
  };
}

async function deriveStepFacets(
  c: WriteCtx,
  stepDir: string,
  {
    out,
    claims,
    resolveConnector,
    snapshot,
  }: {
    out: FacetRows;
    claims: Claims;
    resolveConnector: (name: string) => Promise<string | undefined>;
    snapshot?: FacetSnapshot;
  }
) {
  const { tx, commitId, tenantId } = c;
  const configRaw = snapshot
    ? (snapshot.contentByPath.get(`${stepDir}/config.toml`) ?? null)
    : await readCommitFileContent(c, `${stepDir}/config.toml`);
  // Only a file mid-merge is excused from validity — refusing the write would
  // make its conflict unresolvable.
  const parsed = configRaw
    ? hasConflictMarkers(configRaw)
      ? tryParseStepConfig(configRaw)
      : parseStepConfig(configRaw)
    : defaultStepConfig();
  const config = parsed ?? defaultStepConfig();
  const fragment = snapshot
    ? (snapshot.contentByPath.get(apiFragmentPath(stepDir)) ?? null)
    : await readCommitFileContent(c, apiFragmentPath(stepDir));
  if (fragment !== null && parsed && !hasConflictMarkers(fragment)) {
    const error = validateApiFragment(stepDir, fragment, config);
    if (error) throw userFacingError(error);
  }
  if (config.route) {
    if (isReservedRoute(config.route)) {
      throw userFacingError(`Route "${config.route}" is reserved.`);
    }
    const owner = claims.routes.get(config.route);
    if (owner !== undefined && owner !== stepDir)
      throw userFacingError(
        `Route "${config.route}" is already used by step "${owner}" in this workflow.`
      );
    claims.routes.set(config.route, stepDir);
    const routeAuth = config.routeAuth ?? "space";
    if (config.routeGroups && routeAuth !== "sso") {
      throw userFacingError(
        `Step "${stepDir}": route_sso_groups requires route_auth "sso".`
      );
    }
    out.commitRoutes.push({
      commitId,
      tenantId,
      route: config.route,
      stepDir,
      routeType: config.routeType ?? null,
      routeAuth,
      routeIdp: routeAuth === "sso" ? (config.routeIdp ?? null) : null,
      routeGroups: config.routeGroups ?? null,
      externalId:
        config.routeAuth === "external_id" ? (config.externalId ?? null) : null,
      title: config.title ?? null,
    });
  }
  if (config.cron) {
    let nextRunAt: number;
    try {
      nextRunAt =
        new Cron(config.cron).nextRun()?.getTime() ?? Date.now() + 60000;
    } catch {
      throw userFacingError(
        `Step "${stepDir}" has an invalid cron expression "${config.cron}".`
      );
    }
    out.commitSchedules.push({
      commitId,
      tenantId,
      stepDir,
      cronExpression: config.cron,
      nextRunAt,
    });
  }
  if (config.emailAddress) {
    const owner = claims.emails.get(config.emailAddress);
    if (owner !== undefined && owner !== stepDir)
      throw userFacingError(
        `Email address "${config.emailAddress}" is already used by step "${owner}" in this workflow.`
      );
    claims.emails.set(config.emailAddress, stepDir);
    out.commitEmailAddresses.push({
      commitId,
      tenantId,
      localPart: config.emailAddress,
      stepDir,
    });
  }
  for (const toStep of config.links ?? [])
    out.commitLinks.push({ commitId, tenantId, fromStep: stepDir, toStep });
  const stepFileRows = snapshot
    ? (snapshot.filesByStep.get(stepDir) ?? [])
    : (await loadCommitFileRows(tx, commitId)).filter(
        (f) => stepDirFromPath(f.path) === stepDir
      );
  const contents = snapshot
    ? stepFileRows.map((f) => snapshot.contentByPath.get(f.path) ?? null)
    : await Promise.all(
        stepFileRows.map(async (f) => {
          if (isStepTestPath(f.path) && !isDependencySourceFile(f.path))
            return null;
          const [blob] = await tx.run(
            zql.blobs.where("hash", f.blobHash).where("tenantId", f.tenantId)
          );
          return blob?.content ?? null;
        })
      );
  const sourceFiles: StepFile[] = [];
  stepFileRows.forEach((f, i) => {
    const content = contents[i];
    if (content == null) return;
    if (content && isDependencySourceFile(f.path))
      sourceFiles.push({ path: f.path, content });
  });
  for (const dep of extractStepDependencies(sourceFiles))
    out.commitDependencies.push({
      commitId,
      tenantId,
      stepDir,
      source: dep.source,
      name: dep.name,
      version: dep.version,
    });
  for (const entry of config.connectors ?? []) {
    const connectedAppId = await resolveConnector(entry.name);
    if (!connectedAppId) continue;
    out.connectedAppUsages.push({
      connectedAppId,
      ...usageRef.commitStep(commitId, stepDir),
      createdAt: Date.now(),
      tenantId,
    });
  }
}

export async function syncFacets(c: WriteCtx, stepDir: string) {
  await clearStepFacets(c, stepDir);
  const rows = emptyFacetRows();
  await deriveStepFacets(c, stepDir, {
    out: rows,
    claims: await claimsFromDb(c),
    resolveConnector: connectorResolver(c),
  });
  await writeFacetRows(c, rows);
  await syncCommitConnectorUsages(c);
  await refreshConnectorUsages(c.tx, c.commitId);
}

export async function syncAllFacets(c: WriteCtx): Promise<FacetCounts> {
  // Before the first read: the facets written below are derived from this
  // snapshot, so a concurrent sync committing between the read and the clear
  // would have its rows deleted and replaced from stale files.
  await lockCommitFacets(c.tx, c.commitId);
  const snapshot = await loadFacetSnapshot(c);
  const rows = emptyFacetRows();
  const shared = {
    out: rows,
    claims: { routes: new Map(), emails: new Map() },
    resolveConnector: connectorResolver(c),
    snapshot,
  };
  for (const dir of snapshot.filesByStep.keys())
    await deriveStepFacets(c, dir, shared);
  await clearAllFacets(c);
  await writeFacetRows(c, rows);
  await syncCommitConnectorUsages(c);
  await refreshConnectorUsages(c.tx, c.commitId);
  await c.tx.mutate.workflowCommits.update({
    id: c.commitId,
    readmeDescription: workflowReadmeDescription(
      snapshot.contentByPath.get("README.md") ?? ""
    ),
  });
  return {
    files: snapshot.contentByPath.size,
    stepDirs: snapshot.filesByStep.size,
    facetRows: Object.values(rows).reduce((n, r) => n + r.length, 0),
  };
}
